import type { Catalogue, Module, PresetId } from './catalogue'

/** La copie d'une capacité Nabu propre à un rôle : une partie de ses actions, figée (elle ne suit plus Nabu). */
export interface CustomCapacity {
  id: string
  sourceCapacityId: string
  actions: string[]
}

/**
 * Un rôle = une liste de RÉFÉRENCES à des capacités (Nabu ou personnalisées). Zéro action copiée
 * (décision 5). Une capacité Nabu référencée suit donc ce que Nabu y met : aucune version ni
 * épingle, seule une copie (fork) fige un contenu pour le client.
 */
export interface RoleDef {
  id: string
  /** Saisi pour un rôle client ; le libellé du catalogue pour un rôle Nabu. */
  name: string
  origin: 'system' | 'custom'
  presetId?: PresetId
  sourcePresetId?: PresetId
  capacities: string[]
  createdAt?: string
}

export type RenderState = 'enabled' | 'disabled' | 'hidden'
export type CapacityRenderState = RenderState | 'partial'

/** Donne les actions d'une capacité Nabu. `undefined` = pas une capacité Nabu. */
export type SystemCapacityResolver = (
  capacityId: string
) => readonly string[] | undefined

export const catalogueResolver =
  (catalogue: Catalogue): SystemCapacityResolver =>
  (capacityId) =>
    catalogue.capacitiesById.get(capacityId)?.actions

export const presetRole = (catalogue: Catalogue, id: PresetId): RoleDef => {
  const p = catalogue.presetsById.get(id)
  if (!p) throw new Error(`Unknown preset ${id}`)
  return {
    id: p.id,
    name: p.label,
    origin: 'system',
    presetId: p.id,
    capacities: [...p.capacities],
  }
}

/** Les rôles Nabu, dans l'ordre du catalogue. */
export const systemRoles = (catalogue: Catalogue): RoleDef[] =>
  catalogue.presets.map((p) => presetRole(catalogue, p.id))

/** Union pure : le refus est l'absence, aucune permission négative, aucun conflit à arbitrer. */
export const resolveActions = (
  catalogue: Catalogue,
  role: RoleDef,
  customCapacities: readonly CustomCapacity[] = [],
  resolveSystem: SystemCapacityResolver = catalogueResolver(catalogue)
): Set<string> => {
  const custom = new Map(customCapacities.map((cc) => [cc.id, cc]))
  const out = new Set<string>()
  for (const ref of role.capacities) {
    const fromSystem = resolveSystem(ref)
    const list = fromSystem ?? custom.get(ref)?.actions ?? []
    for (const code of list)
      if (catalogue.actionsByCode.has(code)) out.add(code)
  }
  return out
}

export const isSubset = (inner: Set<string>, outer: Set<string>): boolean => {
  for (const x of inner) if (!outer.has(x)) return false
  return true
}

/** Invité ⊂ Déclarant ⊂ Manager ⊂ Admin — l'invariant de la décision 13. */
export const checkPresetNesting = (
  catalogue: Catalogue
): { ok: boolean; violations: string[] } => {
  const ordered = [...catalogue.presets].sort((x, y) => x.rank - y.rank)
  const violations: string[] = []
  for (let i = 1; i < ordered.length; i += 1) {
    const inner = resolveActions(
      catalogue,
      presetRole(catalogue, ordered[i - 1].id)
    )
    const outer = resolveActions(
      catalogue,
      presetRole(catalogue, ordered[i].id)
    )
    for (const code of inner) {
      if (!outer.has(code)) {
        violations.push(
          `${ordered[i].id} lacks ${code} held by ${ordered[i - 1].id}`
        )
      }
    }
  }
  return { ok: violations.length === 0, violations }
}

/** Peut-on LIRE le module ? Vrai s'il existe une action « read » du module dans l'effectif. */
export const canReadModule = (
  catalogue: Catalogue,
  module: Module,
  effective: Set<string>
): boolean =>
  catalogue.actions.some(
    (x) =>
      x.module === module && x.accessLevel === 'read' && effective.has(x.code)
  )

/**
 * Décision 16 — grisé pour agir, caché pour détruire.
 * - accordée → enabled
 * - module illisible → hidden
 * - supprimer / gérer les droits → hidden
 * - lire, agir ou exécuter dans un module lisible → disabled (avec explication à l'écran)
 */
export const renderState = (
  catalogue: Catalogue,
  actionCode: string,
  effective: Set<string>
): RenderState => {
  if (effective.has(actionCode)) return 'enabled'
  const def = catalogue.actionsByCode.get(actionCode)
  if (!def) return 'hidden'
  if (!canReadModule(catalogue, def.module, effective)) return 'hidden'
  if (
    def.accessLevel === 'delete' ||
    def.accessLevel === 'permissions_management'
  )
    return 'hidden'
  return 'disabled'
}

/** État d'une capacité entière à l'écran : tout accordé, partiel, grisé ou caché. */
export const capacityRenderState = (
  catalogue: Catalogue,
  actions: readonly string[],
  effective: Set<string>
): CapacityRenderState => {
  if (actions.length === 0) return 'hidden'
  const states = actions.map((code) => renderState(catalogue, code, effective))
  if (states.every((s) => s === 'enabled')) return 'enabled'
  if (states.some((s) => s === 'enabled')) return 'partial'
  if (states.some((s) => s === 'disabled')) return 'disabled'
  return 'hidden'
}

/** Le plus petit préréglage qui couvre un ensemble d'actions (règle de bascule, décision 13). */
export const smallestCoveringPreset = (
  catalogue: Catalogue,
  needed: Set<string>
): PresetId | null => {
  const ordered = [...catalogue.presets].sort((x, y) => x.rank - y.rank)
  for (const p of ordered) {
    if (
      isSubset(needed, resolveActions(catalogue, presetRole(catalogue, p.id)))
    )
      return p.id
  }
  return null
}
